import { Observable } from 'rxjs';
import _ from 'lodash';
import { List } from 'immutable';
import moment from 'moment';
import type { ActionsObservable } from 'redux-observable';
import type { Store, AnyAction } from 'redux';
import {
  startSubmit,
  stopSubmit,
  initialize,
  change,
  formValueSelector,
} from 'redux-form/immutable';
import { push, replace } from 'react-router-redux';
import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';
import { EmptyObservable } from 'rxjs/observable/EmptyObservable';

import addressToLocation from 'utils/addressToLocation';
import { getTimeZoneFromGoogle } from 'utils/getTimeZoneFromGoogle';
import { selectOrganization, selectUserLocations, selectUser } from 'entities/user/selectors';
import { selectDeedById } from 'entities/deed/selectors';
import DeedsApi from 'entities/deed/api';
import UserApi from 'entities/user/api';
import { selectCauses } from 'entities/cause/selectors';
import { selectCampaigns } from 'entities/campaign/selectors';
import { selectSkills } from 'entities/skill/selectors';
import { selectForms } from 'entities/form/selectors';
import LocationApi from 'entities/location/api';
import PartnersApi from 'entities/partners/api';
import CauseApi from 'entities/cause/api';
import CampaignApi from 'entities/campaign/api';
import SkillsApi from 'entities/skill/api';
import FormsApi from 'entities/form/api';
import { selectPictures } from 'entities/picture/selectors';
import PicturesApi from 'entities/picture/api';
import { showMessageAction, showErrorAction } from 'containers/Notification/actions';
import { STORE as STORE_USER } from 'entities/user/constants';
import type { State } from 'reducers';
import { logCommunityDeedEvents } from 'containers/DeedFormPage/communityDeedsMetrics';
import { Privileges } from 'utils/privileges';

import { selectActiveStep } from './selectors';
import {
  initSuccessAction,
  initFailedAction,
  saveSuccessAction,
  saveFailedAction,
  setStepperAction,
  fetchPartnersAction,
  fetchPartnersSuccessAction,
  fetchPartnersFailedAction,
  trackEventAction,
  InitAction,
  SaveEventAction,
} from './actions';
import {
  FORM_IDENTIFIER,
  INIT,
  INIT_SUCCESS,
  SAVE_EVENT,
  SAVE_CAMPAIGN,
  SAVE_PROJECT,
  FILE_UPLOADED,
  FETCH_PARTNERS,
  TRACK_EVENT_COMMUNITY_DEEDS,
} from './constants';

const init = (action$: ActionsObservable<InitAction>, store: Store<State>) =>
  action$.ofType(INIT).mergeMap(({ id, duplicate }) => {
    const actions = [];
    const state = store.getState();

    const causes = selectCauses(state);
    if (causes.size === 0) {
      actions.push(CauseApi.fetchAll());
    }

    const campaigns = selectCampaigns(state);
    if (campaigns.size === 0) {
      actions.push(CampaignApi.fetchAll());
    }

    const skills = selectSkills(state);
    if (skills.size === 0) {
      actions.push(SkillsApi.fetchAll());
    }

    const locations = selectUserLocations(state);
    if (locations.size === 0) {
      actions.push(LocationApi.fetchAll());
    }

    const forms = selectForms(state);
    if (forms.size === 0) {
      actions.push(FormsApi.fetchAll());
    }

    const pictures = selectPictures(state);
    if (pictures.size === 0) {
      actions.push(PicturesApi.fetchAll());
    }

    const user = selectUser(state);
    const allowedToEditPastDeeds = user?.hasPrivilege(Privileges.DEED_EDIT_PAST);

    const cleanDeedIfDuplicating = (deed) => {
      let cleanedDeed = deed;
      // `forms` field is populated in the API, we want to convert it back to the list of ids
      if (cleanedDeed.get('forms')) {
        cleanedDeed = cleanedDeed.set(
          'forms',
          cleanedDeed.get('forms').map((item) => item.id)
        );
      }
      cleanedDeed = cleanedDeed.set('organization', cleanedDeed.organization?.id);
      if (cleanedDeed.partner) {
        cleanedDeed = cleanedDeed.set('partner', cleanedDeed.partner.id);
      }
      if (duplicate) {
        cleanedDeed = cleanedDeed.delete('id');
        cleanedDeed = cleanedDeed.set('status', 'draft');
        cleanedDeed = cleanedDeed.set('startingAt', undefined);
        cleanedDeed = cleanedDeed.set('endingAt', undefined);
        cleanedDeed = cleanedDeed.set('categories', cleanedDeed.get('categories').take(3));
        cleanedDeed = cleanedDeed.set('SDGs', cleanedDeed.get('SDGs').take(3));
        cleanedDeed = cleanedDeed.set('campaigns', cleanedDeed.get('campaigns'));
        cleanedDeed = cleanedDeed.set('pillars', cleanedDeed.get('pillars'));
        cleanedDeed = cleanedDeed.set('ERGs', cleanedDeed.get('ERGs'));
        cleanedDeed = cleanedDeed.delete('duration');
        cleanedDeed = cleanedDeed.delete('checkIns');
        cleanedDeed = cleanedDeed.delete('attendees');
        cleanedDeed = cleanedDeed.delete('waitlist');
        cleanedDeed = cleanedDeed.delete('checkIns');
        cleanedDeed = cleanedDeed.delete('ended');
        cleanedDeed = cleanedDeed.delete('externalLinkFollows');

        if (cleanedDeed.get('roles')) {
          cleanedDeed = cleanedDeed.set(
            'roles',
            cleanedDeed.get('roles').map((role) =>
              role.merge({
                applicants: new List(),
                approved: new List(),
                rejected: new List(),
                startingAt: undefined,
                endingAt: undefined,
              })
            )
          );
        }

        if (cleanedDeed.get('milestones')) {
          cleanedDeed = cleanedDeed.set(
            'milestones',
            cleanedDeed.get('milestones').map((milestone) => milestone.delete('completed'))
          );
        }
      } else {
        if (cleanedDeed.startingAt) {
          cleanedDeed = cleanedDeed.set(
            'startingAt',
            utcToZonedTime(cleanedDeed.startingAt, cleanedDeed.timeZone)
          );
        }
        if (cleanedDeed.endingAt) {
          cleanedDeed = cleanedDeed.set(
            'endingAt',
            utcToZonedTime(cleanedDeed.endingAt, cleanedDeed.timeZone)
          );
        }
        if (cleanedDeed.roles) {
          cleanedDeed = cleanedDeed.set(
            'roles',
            cleanedDeed.roles.map((role) => {
              let cleanedRole = role;

              if (role.startingAt && cleanedDeed.timeZone) {
                cleanedRole = cleanedRole.set(
                  'startingAt',
                  utcToZonedTime(cleanedRole.startingAt, cleanedDeed.timeZone)
                );
              }
              if (role.endingAt && cleanedDeed.timeZone) {
                cleanedRole = cleanedRole.set(
                  'endingAt',
                  utcToZonedTime(cleanedRole.endingAt, cleanedDeed.timeZone)
                );
              }

              return cleanedRole;
            })
          );
        }
      }

      return {
        ...cleanedDeed.toObject(),

        companyOrPublic: cleanedDeed.companySubsidiaryAccess
          ? 'shared'
          : !cleanedDeed.requireAuthentication && !cleanedDeed.public
          ? undefined
          : cleanedDeed.requireAuthentication
          ? 'company'
          : 'public',
        timeZone: cleanedDeed.timeZone,
        optedInCompanies: cleanedDeed.optedInCompanies?.map((c) => c.id),
      };
    };

    if (id === 'new' || id === 'past') {
      actions.push(Observable.of(initialize(FORM_IDENTIFIER, { status: 'draft' })));
      // TODO: Remove these pieces of logic for stepper
      actions.push(Observable.of(setStepperAction(0)));
    } else if (id.indexOf('ext-') === 0) {
      actions.push(
        DeedsApi.fetchExternalDeed(id).map((externalDeed) => {
          if (externalDeed.type !== 'Project') {
            return [
              replace(`/deeds/${externalDeed.type.toLowerCase()}s/edit/${id}`),
              initialize(FORM_IDENTIFIER, cleanDeedIfDuplicating(externalDeed)),
              setStepperAction(16), // eslint-disable-line no-nested-ternary
            ];
          }
          return [
            initialize(FORM_IDENTIFIER, cleanDeedIfDuplicating(externalDeed)),
            setStepperAction(16), // eslint-disable-line no-nested-ternary
          ];
        })
      );
    } else {
      const deed = selectDeedById(state, id);
      if (deed) {
        if (
          !deed.manual &&
          !duplicate &&
          deed.ended &&
          deed.status === 'published' &&
          deed.type !== 'Campaign' &&
          !allowedToEditPastDeeds
        ) {
          return Observable.of(
            replace(`/deeds/${deed.pathSlug}/${deed.id}`),
            showErrorAction('Cannot edit a past Deed.')
          );
        }
        actions.push(Observable.of(initialize(FORM_IDENTIFIER, cleanDeedIfDuplicating(deed))));
        actions.push(
          // eslint-disable-next-line no-nested-ternary
          Observable.of(setStepperAction(duplicate ? 6 : deed.manual ? 9 : 15))
        );
      } else {
        actions.push(
          DeedsApi.fetch(id)
            .map((action) => {
              if (
                !action.deed.manual &&
                !duplicate &&
                action.deed.ended &&
                action.deed.status === 'published' &&
                action.deed.type !== 'Campaign' &&
                !allowedToEditPastDeeds
              ) {
                return [
                  replace(`/deeds/${action.deed.pathSlug}/${action.deed.id}`),
                  showErrorAction('Cannot edit a past Deed.'),
                ];
              }
              return [
                action,
                initialize(FORM_IDENTIFIER, cleanDeedIfDuplicating(action.deed)),
                setStepperAction(duplicate ? 6 : action.deed.manual ? 9 : 15), // eslint-disable-line no-nested-ternary
              ];
            })
            .catch((e) => Observable.of(initFailedAction(e), replace('/notFound')))
        );
      }
    }

    return Observable.combineLatest(actions)
      .mergeMap((resultingActions) => [..._.flatten(resultingActions), initSuccessAction()])
      .catch((e) => Observable.of(initFailedAction(e)));
  });

const prefillOrganizationInfo = (action$, store: Store<State>) =>
  action$.ofType(STORE_USER, INIT_SUCCESS).mergeMap(() => {
    const state = store.getState();
    const plainState = state.toJS();
    const selector = formValueSelector(FORM_IDENTIFIER);
    const actions = [];
    const organization = selectOrganization(state);
    if (!organization || organization.isCompany()) {
      return [];
    }
    if (!selector(plainState, 'location')) {
      const activeStep = selectActiveStep(state);
      actions.push(setStepperAction(activeStep === 0 ? 1 : activeStep));
      actions.push(change(FORM_IDENTIFIER, 'location', organization.get('location')));
    }
    ['organizerEmail', 'organizerPhone'].forEach((fieldName) => {
      if (!selector(plainState, fieldName)) {
        actions.push(
          change(FORM_IDENTIFIER, fieldName, organization.get(fieldName.substr(9).toLowerCase()))
        );
      }
    });
    ['categories', 'SDGs'].forEach((fieldName) => {
      const value = selector(plainState, fieldName);
      if (organization.get(fieldName) && (!value || value.length === 0)) {
        actions.push(change(FORM_IDENTIFIER, fieldName, organization.get(fieldName).take(3)));
      }
    });
    return Observable.concat(actions);
  });

const fileUploaded = (action$) =>
  action$
    .ofType(FILE_UPLOADED)
    .mergeMap(({ file }) =>
      Observable.of(change(FORM_IDENTIFIER, 'pictures', new List([file.publicUrl])))
    );

const handleDeedDates = (data) => {
  if (data.endingAt <= data.startingAt) {
    // eslint-disable-next-line no-param-reassign
    data.endingAt = moment(data.endingAt).add(1, 'day').toDate();
  }
  // eslint-disable-next-line no-param-reassign
  data.duration = data.endingAt
    ? Math.floor(moment(data.endingAt).diff(data.startingAt) / 60000)
    : 0;

  return data;
};

type DeedData = {
  id?: string;
  organization?: string;
  communityIds: string[];
  organizingCommunityIds: string[];
};
const saveData = (_data: DeedData, store: Store<State>) => {
  // Clone data object to preven accidentally mutating it during clean up
  const data = { ..._data };
  const noOrganization = !data.organization;
  const state = store.getState();
  const user = selectUser(state);

  if (data.companyOrPublic === 'shared') {
    data.companySubsidiaryAccess = true;
    data.requireAuthentication = true;
    data.public = false;
  } else if (data.companyOrPublic === 'company') {
    data.requireAuthentication = true;
    data.public = false;
    data.companySubsidiaryAccess = false;
  } else if (data.companyOrPublic === 'public') {
    data.requireAuthentication = false;
    data.public = true;
    data.companySubsidiaryAccess = false;
  }

  if (data.type === 'Event' && !data.virtual && !data.externalId && data.externalLink) {
    data.externalLink = '';
  }

  if (data.virtual && data.locationObject) {
    Object.keys(data.locationObject).forEach((key) => {
      data.locationObject[key] = '';
    });
  }

  // For community deed added/removed analytics
  const ogDeed = data.id && selectDeedById(state, data.id);

  const communityIds = data.communityIds || [];
  const originalCommunityIds = (ogDeed && (ogDeed.communityIds?.toJS() as string[])) || [];

  const organizingCommunityIds = data.organizingCommunityIds || [];
  const originalOrganizingCommunityIds =
    (ogDeed && (ogDeed.organizingCommunityIds?.toJS() as string[])) || [];

  const addedCommunityIds: string[] = _.difference(communityIds, originalCommunityIds);
  const removedCommunityIds: string[] = _.difference(originalCommunityIds, communityIds);

  const addedOrganizingCommunityIds: string[] = _.difference(
    organizingCommunityIds,
    originalOrganizingCommunityIds
  );
  const removedOrganizingCommunityIds: string[] = _.difference(
    originalOrganizingCommunityIds,
    organizingCommunityIds
  );

  let saveDataObservable;
  const persistData = () => {
    const persistCall =
      data.id && data.id.indexOf('ext-') !== 0
        ? DeedsApi.update(data.id, data)
        : DeedsApi.create(data);
    return persistCall.mergeMap((action) =>
      [
        action,
        noOrganization && fetchPartnersAction(),
        trackEventAction({
          addedCommunityIds,
          removedCommunityIds,
          addedOrganizingCommunityIds,
          removedOrganizingCommunityIds,
          deedId: data.id,
          userId: user?.id,
        }),
        saveSuccessAction(action.deed),
        stopSubmit(FORM_IDENTIFIER),

        push(`/deeds/${action.deed.pathSlug}/${action.deed.id}`),
        showMessageAction(data.id ? 'Changes Saved!' : 'Deed Created!'),
      ].filter(Boolean)
    );
  };

  if (data.virtual || data.type === 'Campaign') {
    data.location = null;
    data.locationObject = {};
    if (!data.timeZone) {
      data.timeZone = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone;
    }
  }

  const locations = selectUserLocations(store.getState()).toArray();
  const { allowedLocations } = data;

  const countries = [...new Set(locations.map((location) => location.country))];
  const locationStates = [...new Set(locations.map((location) => location.state))];

  // eslint-disable-next-line no-param-reassign
  data.allowedCountries = countries
    .map((country) => {
      if (
        allowedLocations?.length &&
        !locations.find(
          (location) => location.country === country && !allowedLocations.includes(location.id)
        )
      ) {
        return country;
      }
      return undefined;
    })
    .filter(Boolean);

  data.allowedStates = locationStates
    .map((locationState) => {
      if (
        allowedLocations?.length &&
        !locations.find(
          (location) => location.state === locationState && !allowedLocations.includes(location.id)
        )
      ) {
        const stateCountry = locations.find((location) => location.state === locationState).country;
        return `${stateCountry}_${locationState}`;
      }
      return undefined;
    })
    .filter(Boolean);

  if (data.deedType !== 'Campaign' && data.startingAt) {
    if (!data.timeZone && !_.isEmpty(data.location)) {
      saveDataObservable = getTimeZoneFromGoogle(data.location, data.startingAt).mergeMap(
        (result) => {
          if (!result) {
            throw Error('Error getting timezone data');
          }
          data.timeZone = result.timeZoneId; // eslint-disable-line no-param-reassign
        }
      );
    }

    // startingAt and endingAt has at this point the time the user picked, but the timeZone
    // of the user's machine (which is at this point basically a wrong time, because it should be data.timeZone based).
    // The following block transforms this wrong times to the correct one, based on the data.timeZone
    if (data.startingAt) {
      // eslint-disable-next-line no-param-reassign
      data.startingAt = zonedTimeToUtc(
        data.startingAt?.toDate?.() ?? data.startingAt,
        data.timeZone
      );
    }
    if (data.endingAt) {
      // eslint-disable-next-line no-param-reassign
      data.endingAt = zonedTimeToUtc(data.endingAt?.toDate?.() ?? data.endingAt, data.timeZone);
    }
    if (data.roles) {
      data.roles.map((role) => {
        if (role.startingAt && data.timeZone) {
          // eslint-disable-next-line no-param-reassign
          role.startingAt = zonedTimeToUtc(
            role.startingAt?.toDate?.() ?? role.startingAt,
            data.timeZone
          );
        }
        if (role.endingAt && data.timeZone) {
          // eslint-disable-next-line no-param-reassign
          role.endingAt = zonedTimeToUtc(role.endingAt?.toDate?.() ?? role.endingAt, data.timeZone);
        }

        if (role?.requiredAmount) {
          // eslint-disable-next-line no-param-reassign
          role.requiredAmount = Number(role.requiredAmount);
        }

        return role;
      });
    }
    return persistData();
  } else {
    saveDataObservable = persistData();
  }

  return saveDataObservable.catch((e) => {
    if (e.responseBody) {
      return Observable.of(e.responseBody).mergeMap((errorData) => [
        saveFailedAction(errorData, 'saveFailed'),
        stopSubmit(FORM_IDENTIFIER),
        showErrorAction(
          `An error occurred during saving, please try again. ${[
            Object.keys(errorData.errors || {}).length > 0
              ? Object.keys(errorData.errors).map((key) => `${errorData.errors[key].message}`)
              : errorData.message,
          ].join(' – ')}`
        ),
      ]);
    }
    return Observable.of(
      saveFailedAction(e, 'saveFailed'),
      stopSubmit(FORM_IDENTIFIER),
      showErrorAction('An error occurred during saving, please try again.')
    );
  });
};

const setOrganization = (deedData, store: Store<State>) => {
  const organization = selectOrganization(store.getState());

  const noOrganization =
    !deedData.organization && !['Campaign', 'BaseEvent'].includes(deedData.deedType);
  const data = { ...deedData };

  if (data.deedType === 'BaseEvent') {
    data.organization = organization.id;
  }

  if (organization.isCompany()) {
    data.partner = organization.id;
    const { partnerName, partnerEmail, partnerEin, partnerPhone, partnerLocation } = data;
    if (noOrganization && partnerName && partnerEmail) {
      const createOrganization = (partnerData) =>
        UserApi.createOrganizer(partnerData, 'nonprofit')
          .mergeMap((user) => {
            data.organization = user.organization.id;
            return Observable.from(PartnersApi.create(user.organization.id)).mergeMap(() =>
              saveData(data, store)
            );
          })
          .catch((e) => {
            if (e.responseBody) {
              return Observable.of(e.responseBody).mergeMap((errorData) => [
                saveFailedAction(errorData, 'organizationCreationFailed'),
                stopSubmit(FORM_IDENTIFIER),
                showErrorAction(
                  `Nonprofit partner could not be created. ${[
                    Object.keys(errorData.errors || {}).length > 0
                      ? Object.keys(errorData.errors).map(
                          (key) => `${errorData.errors[key].message}`
                        )
                      : errorData.message,
                  ].join(' – ')}`
                ),
              ]);
            }
            return Observable.of(
              saveFailedAction(e, 'organizationCreationFailed'),
              stopSubmit(FORM_IDENTIFIER),
              showErrorAction('Nonprofit partner could not be created.')
            );
          });

      const partnerData = {
        name: !/ /g.test(partnerName) ? `${partnerName} Organization` : partnerName, // ensure that there is a last name
        email: partnerEmail,
        organization: {
          name: partnerName,
          email: partnerEmail,
          ein: partnerEin,
          phone: (partnerPhone || '').replace(/[^0-9]/g, ''),
        },
      };

      if (partnerLocation) {
        return addressToLocation(partnerLocation).mergeMap(({ location, locationObject }) => {
          partnerData.organization.location = location;
          partnerData.organization.locationObject = locationObject;
          return createOrganization(partnerData);
        });
      }
      return createOrganization(partnerData);
    }

    return saveData(data, store);
  }

  data.organization = organization.id;
  data.partner = data.partner || null;
  return saveData(data, store);
};

const saveEvent = (action$: ActionsObservable<SaveEventAction>, store: Store<State>) =>
  action$.ofType(SAVE_EVENT).mergeMap((payload) =>
    Observable.concat(
      Observable.of(startSubmit(FORM_IDENTIFIER)),
      (payload.data.location ? addressToLocation(payload.data.location) : Observable.of({}))
        .mergeMap(({ location, locationObject }) => {
          let data = { ...payload.data };

          data.location = location || {};
          data.locationObject = locationObject || {};
          data.organizerPhone = data.organizerPhone
            ? `+${data.organizerPhone.replace(/[^0-9]/g, '')}`
            : ''; // remove non-number characters from phone number

          delete data.shareLink;
          delete data.attendees;
          delete data.invites;
          delete data.isSharedWithSubsidiary;
          delete data.optedIn;
          data.organization =
            data.organization?.nonpofitId || data.organization?.id || data.organization;

          // Handle any start/end date specifics
          data = handleDeedDates(data);

          // organizer & partner information - This will save the form
          return setOrganization(data, store);
        })
        .catch((err) => {
          const errorMessage = err?.responseBody?.errors
            ? err?.responseBody?.errors
                ?.map((item: Record<string, unknown>) => item.message)
                .join(', ')
            : err?.responseBody?.message;
          return Observable.of(
            saveFailedAction(err, 'addressInvalid', {
              address: payload.data.location,
            }),
            stopSubmit(FORM_IDENTIFIER),
            showErrorAction(errorMessage || 'Address could not be found.')
          );
        })
    )
  );

const saveCampaign = (action$, store: Store<State>) =>
  action$.ofType(SAVE_CAMPAIGN).mergeMap((payload) =>
    Observable.concat(
      Observable.of(startSubmit(FORM_IDENTIFIER)).switchMap(() => {
        let data = { ...payload.data };
        data.organizerPhone = data.organizerPhone
          ? `+${data.organizerPhone.replace(/[^0-9]/g, '')}`
          : ''; // remove non-number characters from phone number

        delete data.shareLink;
        delete data.donations;
        delete data.isSharedWithSubsidiary;
        delete data.optedIn;
        data.organization =
          data.organization?.nonpofitId || data.organization?.id || data.organization;

        // Only send modified properties for already imported external nonprofits
        if (data.id && data.nonprofits?.length > 0) {
          const state = store.getState();
          const deed = selectDeedById(state, data.id);
          data.nonprofits = data.nonprofits.map((nonprofit, index) => {
            if (nonprofit.externalId && !nonprofit.id.includes('ext-')) {
              const editableKeys = ['name', 'brief', 'mainPicture'];
              const originalNonprofit = _.pick(deed.nonprofits.get(index)?.toJS(), editableKeys);
              if (originalNonprofit) {
                const filteredNonprofit = _.pick(nonprofit, editableKeys);
                const diffKeys = Object.keys(filteredNonprofit).filter(
                  (key) => filteredNonprofit[key] !== originalNonprofit[key]
                );
                const modifiedNonprofit = _.pick(filteredNonprofit, diffKeys);
                return {
                  ...modifiedNonprofit,
                  id: nonprofit.id,
                  externalId: nonprofit.externalId,
                };
              }
            }
            return nonprofit;
          });
        }

        // Handle any start/end date specifics
        data = handleDeedDates(data, store);

        // organizer & partner information - This will save the form
        return setOrganization(data, store);
      })
    )
  );

const saveProject = (action$, store: Store<State>) =>
  action$.ofType(SAVE_PROJECT).mergeMap((payload) =>
    Observable.concat(
      Observable.of(startSubmit(FORM_IDENTIFIER)).switchMap(() => {
        if (payload.data.location) {
          return addressToLocation(payload.data.location)
            .mergeMap(({ location, locationObject }) => {
              let data = { ...payload.data };
              data.location = location;
              data.locationObject = locationObject;
              data.organizerPhone = data.organizerPhone
                ? `+${data.organizerPhone.replace(/[^0-9]/g, '')}`
                : ''; // remove non-number characters from phone number

              delete data.shareLink;
              delete data.attendees;
              delete data.invites;
              delete data.isSharedWithSubsidiary;
              delete data.optedIn;
              data.organization =
                data.organization?.nonpofitId || data.organization?.id || data.organization;

              // Handle any start/end date specifics
              data = handleDeedDates(data);

              // organizer & partner information - This will save the form
              return setOrganization(data, store);
            })
            .catch((err) => {
              const errorMessage = err?.responseBody?.errors
                ? err?.responseBody?.errors
                    ?.map((item: Record<string, unknown>) => item.message)
                    .join(', ')
                : err?.responseBody?.message;
              return Observable.of(
                saveFailedAction(err, 'addressInvalid', {
                  address: payload.data.location,
                }),
                stopSubmit(FORM_IDENTIFIER),
                showErrorAction(errorMessage || 'Address could not be found.')
              );
            });
        }

        let data = { ...payload.data };
        data.organizerPhone = data.organizerPhone
          ? `+${data.organizerPhone.replace(/[^0-9]/g, '')}`
          : ''; // remove non-number characters from phone number

        delete data.shareLink;
        delete data.attendees;

        // Handle any start/end date specifics
        data = handleDeedDates(data, store);

        // organizer & partner information - This will save the form
        return setOrganization(data, store);
      })
    )
  );

const fetchPartners = (action$) =>
  action$.ofType(FETCH_PARTNERS).mergeMap(({ search, countryCode, acceptsDonations }) =>
    PartnersApi.fetch({ search, countryCode, acceptsDonations })
      .mergeMap((resultingAction) => [resultingAction, fetchPartnersSuccessAction()])
      .catch((e) => Observable.of(fetchPartnersFailedAction(e)))
  );

const trackEvent = (action$: AnyAction) =>
  action$
    .ofType(TRACK_EVENT_COMMUNITY_DEEDS)
    .flatMap(
      (arg: {
        addedCommunityIds: string[];
        removedCommunityIds: string[];
        addedOrganizingCommunityIds: string[];
        removedOrganizingCommunityIds: string[];
        deedId: string;
        userId: string;
      }) => {
        logCommunityDeedEvents(arg);
        return new EmptyObservable();
      }
    );

export default [
  init,
  prefillOrganizationInfo,
  fileUploaded,
  saveEvent,
  saveCampaign,
  saveProject,
  fetchPartners,
  trackEvent,
];
